import type { SceneI2VAudit, SceneI2VProviderAttempt } from "./scriptStory";

export type AttemptTone = "success" | "failure" | "pending";

const SUCCESS_STATUSES = new Set([
  "accepted",
  "completed",
  "reused",
  "succeeded",
  "uploaded",
]);
const FAILURE_STATUSES = new Set([
  "cancelled",
  "capacity_skipped",
  "failed",
  "qc_rejected",
  "timed_out",
  "timeout",
]);
const FAILURE_QC_DISPOSITIONS = new Set([
  "not_uploaded",
  "qc_rejected",
  "rejected",
  "rejected_exhausted",
]);
const SUCCESS_QC_DISPOSITIONS = new Set(["accepted"]);

export function attemptTone(attempt: SceneI2VProviderAttempt): AttemptTone {
  const status = attempt.lifecycleStatus?.toLowerCase() ?? "";
  const qcDisposition = attempt.qcDisposition?.toLowerCase() ?? "";
  if (
    FAILURE_QC_DISPOSITIONS.has(qcDisposition) ||
    FAILURE_STATUSES.has(status) ||
    Boolean(attempt.errorCode || attempt.errorMessage)
  ) {
    return "failure";
  }
  if (
    SUCCESS_STATUSES.has(status) ||
    SUCCESS_QC_DISPOSITIONS.has(qcDisposition)
  ) {
    return "success";
  }
  return "pending";
}

export function isStaticMotionFallback(audit: SceneI2VAudit) {
  if (audit.staticFallbackApplied !== undefined) {
    return audit.staticFallbackApplied;
  }
  if (audit.finalOutputKind === "provider_generated_video") return false;
  if (audit.finalOutputKind === "source_static_motion") return true;

  // Compatibility for manifests written before final_output_kind existed. A
  // prompted StaticImageScene retained its script type even when Veo/Kling
  // successfully supplied the selected clip. The accepted attempt is stronger
  // evidence than that legacy type label.
  const attempts = audit.attempts ?? [];
  if (attempts.some((attempt) => attemptTone(attempt) === "success")) {
    return false;
  }
  if (attempts.length > 0) {
    return audit.finalSceneType === "StaticImageScene";
  }
  // Without attempt/outcome evidence, the old final_scene_type is ambiguous:
  // it may be the source script type rather than the realized asset type.
  return (
    audit.finalSceneType === "StaticImageScene" &&
    Boolean(audit.fallbackReason || audit.raiFiltered)
  );
}

function readableRoute(provider: string) {
  return provider
    .replace("veo_gemini_lite", "Veo Lite route")
    .replace("veo_gemini_fast", "Veo fast route")
    .replace("veo_gemini", "Veo standard route")
    .replace("kling_selector", "Kling selector")
    .replace("kling_direct", "Kling Direct")
    .replace("kling_piapi", "Kling via PiAPI")
    .replaceAll("_", " ");
}

export function providerDisplay(attempt: SceneI2VProviderAttempt) {
  const provider = attempt.concreteProvider ?? attempt.provider ?? "Provider";
  const model = attempt.model?.toLowerCase() ?? "";
  const name = model.includes("veo-3.1-lite")
    ? "Veo 3.1 Lite"
    : model.includes("veo-3.1-fast")
      ? "Veo 3.1 Fast"
      : model.includes("veo-3.1")
        ? "Veo 3.1 Standard"
        : model.includes("kling-v3")
          ? "Kling 3.0"
          : model.includes("kling-v2-6")
            ? "Kling 2.6"
            : readableRoute(provider);
  const route = attempt.provider ? readableRoute(attempt.provider) : null;
  const concrete =
    attempt.concreteProvider && attempt.concreteProvider !== attempt.provider
      ? attempt.concreteProvider === "veo_gemini"
        ? "Google Veo backend"
        : readableRoute(attempt.concreteProvider)
      : null;
  const details = [...new Set([route, concrete, attempt.mode].filter(Boolean))];
  return {
    name,
    modelId: attempt.model ?? null,
    modelVersion: attempt.modelVersion ?? null,
    details: details.join(" · "),
  };
}
